import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "under_review", "awaiting_customer", "in_progress", "quote_sent", "completed", "closed"];
const LABEL = { new: "New", under_review: "Under Review", awaiting_customer: "Awaiting Customer", in_progress: "In Progress", quote_sent: "Quote Sent", completed: "Completed", closed: "Closed" };

export default function AdminRingRequests() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const load = () => api.get("/admin/ring-requests", { params: { status: status || undefined, q: q || undefined } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [status]);

  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Custom Ring Requests</h1>
      <div className="flex gap-3 mb-5">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 flex gap-3">
          <input data-testid="ring-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search request #, name or email" className="flex-1 h-10 px-3 border border-neutral-300 rounded text-sm" />
          <button className="px-5 h-10 bg-neutral-900 text-white rounded text-sm">Search</button>
        </form>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-3 border border-neutral-300 rounded text-sm bg-white">
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
        </select>
      </div>
      {rows.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-16 text-center text-neutral-500">No ring requests yet.</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm"><thead className="bg-neutral-50 text-neutral-500 text-xs uppercase"><tr><th className="text-left p-4">Request</th><th className="text-left p-4">Customer</th><th className="text-left p-4">Email</th><th className="text-left p-4">Status</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} data-testid={`ring-row-${r.request_number}`} onClick={() => setSel(r)} className="border-t border-neutral-100 cursor-pointer hover:bg-neutral-50">
                <td className="p-4 font-medium">{r.request_number}</td><td className="p-4">{r.name}</td><td className="p-4 text-neutral-600">{r.email}</td>
                <td className="p-4"><span className="text-xs bg-neutral-100 px-2 py-1 rounded">{LABEL[r.status] || r.status}</span></td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      {sel && <Drawer req={sel} onClose={() => setSel(null)} reload={load} setSel={setSel} />}
    </div>
  );
}

function Drawer({ req, onClose, reload, setSel }) {
  const [r, setR] = useState(req);
  const [note, setNote] = useState("");
  const upd = async (patch) => { const { data } = await api.put(`/admin/ring-requests/${r.id}`, patch); setR(data); setSel(data); reload(); toast.success("Updated."); };
  return (
    <div className="fixed inset-0 bg-black/50 z-[200] flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="ring-drawer">
        <div className="flex justify-between mb-6"><h2 className="font-serif text-2xl">{r.request_number}</h2><button onClick={onClose}><X size={22} /></button></div>
        <div className="text-sm space-y-1 mb-5 text-neutral-700">
          <div><strong>Name:</strong> {r.name}</div><div><strong>Email:</strong> <a className="text-blue-600 underline" href={`mailto:${r.email}`}>{r.email}</a></div>
          <div><strong>Phone:</strong> <a className="text-blue-600 underline" href={`tel:${r.phone}`}>{r.phone}</a></div>
          <div><strong>Budget:</strong> {r.budget_range || `${r.budget_min || "—"} – ${r.budget_max || "—"}`}</div>
          <div><strong>Submitted:</strong> {(r.created_at || "").slice(0, 16).replace("T", " ")}</div>
        </div>
        <div className="mb-4"><div className="text-xs uppercase text-neutral-500 mb-1">Ring Design Requirements</div><p className="text-sm bg-neutral-50 p-3 rounded">{r.details}</p></div>
        {r.inspiration_image && <div className="mb-4"><div className="text-xs uppercase text-neutral-500 mb-1">Inspiration</div><img src={r.inspiration_image} alt="" className="w-40 rounded border" /><a href={r.inspiration_image} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline block mt-1">Open / download</a>{r.inspiration_note && <p className="text-sm mt-2">{r.inspiration_note}</p>}</div>}
        <div className="mb-5"><div className="text-xs uppercase text-neutral-500 mb-1">Status</div>
          <select data-testid="ring-status-select" value={r.status} onChange={(e) => upd({ status: e.target.value })} className="w-full h-10 px-2 border border-neutral-300 rounded text-sm">
            {STATUSES.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
          </select>
        </div>
        <div className="text-xs uppercase text-neutral-500 mb-1">Internal Notes</div>
        {(r.notes || []).map((n, i) => <div key={i} className="text-sm bg-neutral-50 p-2 rounded mb-1">{n.text} <span className="text-xs text-neutral-400">— {n.by}</span></div>)}
        <div className="flex gap-2 mt-2"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add note…" className="flex-1 h-10 px-3 border border-neutral-300 rounded text-sm" /><button onClick={() => { if (note) { upd({ note }); setNote(""); } }} className="px-4 bg-neutral-900 text-white rounded text-sm">Add</button></div>
      </div>
    </div>
  );
}
